'use client'

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { toSnakeCase } from '@nwwp/utils'
import type { FunnelSchema } from '~/lib/schemas/funnel-schema'

type FunnelState = {
  data: Partial<FunnelSchema>
  tracking: Record<string, string>
  trackingInitialized: boolean
  setData: (data: Partial<FunnelSchema>) => void
  calculateInvestableAssetsRange: (currentFormFields: Partial<FunnelSchema>) => void
  setTracking: (tracking: Record<string, string>) => void
  isQualifiedForNWWP: () => boolean
  _hasHydrated: boolean
}

export const useFunnelStore = create<FunnelState>()(
  persist(
    (set, get) => ({
      data: {},
      tracking: {},
      trackingInitialized: false,
      _hasHydrated: false,

      setData: (data) => set({ data: { ...get().data, ...data } }),

      setTracking: (tracking) => {
        const updatedTracking = { ...get().tracking }

        Object.entries(tracking).forEach(([key, value]) => {
          updatedTracking[toSnakeCase(key)] = value
        })

        set({ tracking: updatedTracking, trackingInitialized: true })
      },

      calculateInvestableAssetsRange: (currentFormFields) => {
        const data = get().data
        const aum = Number({ ...data, ...currentFormFields }.investments) || 0

        if (aum < 100_000) return set({ data: { ...data, investable_assets_range: 'UNDER_100K', aum } })
        if (aum < 250_000) return set({ data: { ...data, investable_assets_range: 'FROM_100_TO_250K', aum } })
        if (aum < 500_000) return set({ data: { ...data, investable_assets_range: 'FROM_250_TO_500K', aum } })
        if (aum < 1_000_000) return set({ data: { ...data, investable_assets_range: 'FROM_500K_TO_1M', aum } })

        return set({ data: { ...data, investable_assets_range: 'OVER_1M', aum } })
      },

      isQualifiedForNWWP: () => {
        const { investable_assets_range, household_income, age } = get().data
        const isBetween27And55 = age && age >= 27 && age <= 55
        const isUnder100K = investable_assets_range === 'UNDER_100K'
        const hasMinIncome = household_income && household_income >= 100_000

        return Boolean(isBetween27And55 && (!isUnder100K || hasMinIncome))
      },
    }),
    {
      name: 'funnel_storage',
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: (state) => {
        if (state) state._hasHydrated = true
      },
    }
  )
)
